// Page Tâches Partagées - Gestion des tâches d'équipe avec assignation
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckSquare, Plus, Filter, User, Calendar, Flag,
  MoreVertical, Trash2, Edit2, Check, Clock, Loader2,
  ChevronDown, Users, ArrowLeft, Sparkles, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = {
  low: { label: 'Basse', color: '#6B7280', bg: 'bg-gray-100' },
  medium: { label: 'Moyenne', color: '#F59E0B', bg: 'bg-yellow-100' },
  high: { label: 'Haute', color: '#EF4444', bg: 'bg-red-100' },
  urgent: { label: 'Urgente', color: '#DC2626', bg: 'bg-red-200' }
};

const STATUSES = {
  todo: { label: 'À faire', color: '#6B7280', bg: 'bg-gray-100' },
  in_progress: { label: 'En cours', color: '#3B82F6', bg: 'bg-blue-100' },
  done: { label: 'Terminé', color: '#10B981', bg: 'bg-green-100' }
};

export default function TachesProPage() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: null, assignee: null });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTasks();
      fetchStats();
    }
  }, [selectedTeam, filter]);

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
        if (data.length > 0) {
          setSelectedTeam(data[0]);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    if (!selectedTeam) return;
    try {
      let url = `${API_URL}/api/pro/teams/${selectedTeam.id}/tasks`;
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.assignee) params.append('assignee_id', filter.assignee);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchStats = async () => {
    if (!selectedTeam) return;
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/tasks/stats`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/tasks/${taskId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(newStatus === 'done' ? 'Tâche terminée !' : 'Statut mis à jour');
        fetchTasks();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteTask = async (taskId) => {
    if (!confirm('Supprimer cette tâche ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/tasks/${taskId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        toast.success('Tâche supprimée');
        fetchTasks();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Grouper les tâches par statut
  const tasksByStatus = {
    todo: tasks.filter(t => t.status === 'todo'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8650A]" />
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Aucune équipe</h3>
          <p className="text-gray-500">Crée d'abord une équipe pour gérer des tâches partagées</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Tâches partagées</h1>
          <p className="text-gray-500 text-sm">Gère les tâches de ton équipe</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Sélecteur d'équipe */}
          <select
            value={selectedTeam?.id || ''}
            onChange={(e) => {
              const team = teams.find(t => t.id === e.target.value);
              setSelectedTeam(team);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
          >
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Nouvelle tâche</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-[#1E3A5F]">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">À faire</p>
            <p className="text-2xl font-bold text-gray-600">{stats.todo}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">En cours</p>
            <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Terminées</p>
            <p className="text-2xl font-bold text-green-600">{stats.done}</p>
            <p className="text-xs text-gray-400">{stats.completion_rate}% complété</p>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(STATUSES).map(([statusKey, statusInfo]) => (
          <div key={statusKey} className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${statusInfo.bg}`} style={{ backgroundColor: statusInfo.color }} />
                <h3 className="font-semibold text-[#1E3A5F]">{statusInfo.label}</h3>
                <span className="text-sm text-gray-500">({tasksByStatus[statusKey].length})</span>
              </div>
            </div>
            
            <div className="space-y-3">
              {tasksByStatus[statusKey].map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onStatusChange={updateTaskStatus}
                  onEdit={() => setEditingTask(task)}
                  onDelete={() => deleteTask(task.id)}
                />
              ))}
              
              {tasksByStatus[statusKey].length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Aucune tâche
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal création/édition */}
      {(showCreateModal || editingTask) && (
        <TaskModal
          task={editingTask}
          teamId={selectedTeam?.id}
          members={selectedTeam?.members || []}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTask(null);
          }}
          onSave={() => {
            fetchTasks();
            fetchStats();
            setShowCreateModal(false);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
}

// Composant carte tâche
function TaskCard({ task, onStatusChange, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const priorityInfo = PRIORITIES[task.priority] || PRIORITIES.medium;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-[#1E3A5F] flex-1 pr-2">{task.title}</h4>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <MoreVertical size={16} className="text-gray-400" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[120px]">
                <button
                  onClick={() => { onEdit(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Edit2 size={14} /> Modifier
                </button>
                <button
                  onClick={() => { onDelete(); setShowMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                >
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {task.description && (
        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{task.description}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Priorité */}
          <span className={`text-xs px-2 py-1 rounded-full ${priorityInfo.bg}`} style={{ color: priorityInfo.color }}>
            {priorityInfo.label}
          </span>
          
          {/* Date d'échéance */}
          {task.due_date && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Calendar size={12} />
              {new Date(task.due_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>

        {/* Assigné */}
        {task.assignee && (
          <div className="w-7 h-7 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-xs font-medium" title={task.assignee.name}>
            {task.assignee.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
      </div>

      {/* Actions rapides de statut */}
      {task.status !== 'done' && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
          {task.status === 'todo' && (
            <button
              onClick={() => onStatusChange(task.id, 'in_progress')}
              className="flex-1 text-xs py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
            >
              Commencer
            </button>
          )}
          <button
            onClick={() => onStatusChange(task.id, 'done')}
            className="flex-1 text-xs py-1.5 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
          >
            <Check size={12} /> Terminer
          </button>
        </div>
      )}
    </div>
  );
}

// Modal création/édition tâche - Version mobile plein écran / Desktop centré
function TaskModal({ task, teamId, members, onClose, onSave }) {
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [assigneeId, setAssigneeId] = useState(task?.assignee_id || '');
  const [dueDate, setDueDate] = useState(task?.due_date?.split('T')[0] || '');
  const [priority, setPriority] = useState(task?.priority || 'medium');
  const [saving, setSaving] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!isMobile) return;
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isMobile]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);

    try {
      const url = task 
        ? `${API_URL}/api/pro/teams/${teamId}/tasks/${task.id}`
        : `${API_URL}/api/pro/teams/${teamId}/tasks`;
      
      const res = await fetch(url, {
        method: task ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({
          title,
          description: description || null,
          assignee_id: assigneeId || null,
          due_date: dueDate || null,
          priority
        })
      });

      if (res.ok) {
        toast.success(task ? 'Tâche mise à jour' : 'Tâche créée');
        onSave();
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[15px] font-bold text-gray-900">
            {task ? 'Modifier la tâche' : 'Nouvelle tâche'}
          </h1>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Appeler le client Dupont"
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Détails de la tâche..."
                rows={3}
                className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Assigner à</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
              >
                <option value="">Non assigné</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user?.name || 'Membre'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Échéance</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Priorité</label>
              <div className="flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {Object.entries(PRIORITIES).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => setPriority(key)}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold border-2 transition-all whitespace-nowrap ${
                      priority === key 
                        ? 'bg-orange-500 text-white border-orange-500' 
                        : 'bg-white border-gray-200 text-gray-500'
                    }`}
                  >
                    {info.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
          >
            <Sparkles size={16} />
            Créer avec Abracada
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={!title.trim() || saving}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {task ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10002] p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#1E3A5F]">
            {task ? 'Modifier la tâche' : 'Nouvelle tâche'}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Appeler le client Dupont"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E8650A] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Détails de la tâche..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E8650A] focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigner à</label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="">Non assigné</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.user?.name || 'Membre'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Échéance</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
            <div className="flex gap-2">
              {Object.entries(PRIORITIES).map(([key, info]) => (
                <button
                  key={key}
                  onClick={() => setPriority(key)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    priority === key 
                      ? 'border-[#E8650A] bg-[#E8650A]/10' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ color: priority === key ? info.color : '#6B7280' }}
                >
                  {info.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
            style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
          >
            <Sparkles size={16} />
            Demander à Abra
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="px-4 py-2 bg-[#E8650A] text-white rounded-lg hover:bg-[#d55a09] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            {task ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
